import React, { useContext, useRef, useEffect, useState } from 'react';
import { cssMap } from '@compiled/react';
import Markdown from 'markdown-to-jsx';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { LinkButton } from '@atlaskit/button/new';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AIStreamLoading } from '@atlassian/ai-components';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { PageCardContext } from '../PageCardContext';
import { PageCardsContext } from '../PageCardsContext';

import { AISnippetFooter } from './AISnippet/AISnippetFooter';

const i18n = defineMessages({
	seeMore: {
		id: 'page-card.popup-view-with-ai-snippet.see-more',
		defaultMessage: 'See more',
		description: 'Text that shows when the AI snippet is truncated',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		textAlign: 'left',
		flex: '2 0 auto',
		overflowWrap: 'break-word',
		font: token('font.body.small'),
		minHeight: '64px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		pre: {
			// We need this so code wraps in the preview, without it the code will overflow the card
			whiteSpace: 'pre-wrap',
		},
	},
	loading: {
		minHeight: '96px',
		display: 'flex', // Use flexbox
		alignItems: 'center', // Center vertically
		height: '100%', // Ensure it takes the full height of the parent container
		textAlign: 'justify',
		flex: '2 0 auto',
		wordWrap: 'break-word',
		font: token('font.body.small'),
	},
	truncatedText: {
		display: '-webkit-box',
		overflow: 'hidden',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 4,
		overflowWrap: 'break-word',
		font: token('font.body.small'),
		'&::before': {
			// We need this to make sure the see more link is on the same line as the text
			content: '',
			float: 'right',
			height: '3lh',
		},
	},
	fullText: {
		display: '-webkit-box',
		overflowWrap: 'break-word',
		overflow: 'hidden',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 5,
		font: token('font.body.small'),
	},
	seeMore: {
		textDecoration: 'none',
		padding: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		a: {
			textDecoration: 'none',
			font: token('font.body.small'),
			padding: token('space.0'),
			height: 'inherit',
			fontWeight: token('font.weight.bold'),
			float: 'right',
			clear: 'both',
		},
	},
	snippet: {
		minHeight: '96px',
	},
});

type ExcerptWithAISnippetProps = {
	loading: boolean;
	snippet?: string;
	isEndOfPage?: boolean;
};

export const ExcerptWithAISnippetV2 = ({
	loading,
	snippet,
	isEndOfPage = false,
}: ExcerptWithAISnippetProps) => {
	const { width } = useContext(PageCardsContext);
	const { id: contentId, excerpt } = useContext(PageCardContext);
	const { trackAIResultView } = useAIEventsInstrumentation();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const snippetRef = useRef<HTMLDivElement | null>(null);

	const [isExpanded, setIsExpanded] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const content = snippet || excerpt;
	const contentValueRef = useRef(content);

	useEffect(() => {
		if (!loading) {
			contentValueRef.current = content;
			if (snippetRef.current) {
				const isOverflowing = snippetRef.current.scrollHeight > snippetRef.current.offsetHeight;
				setIsTruncated(isOverflowing);
			}
		}
	}, [content, loading]);

	const { getDwellTime } = usePopupDwellTime(snippetRef, 250);

	useEffect(() => {
		return () => {
			const dwellTime = getDwellTime();
			if (dwellTime > 0) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'dwelled',
						actionSubject: 'aiSnippetHoverCard',
						source: isEndOfPage ? 'endOfPageRecommendations' : 'pageTree',
						actionSubjectId: isEndOfPage ? 'endOfPageRecommendations' : 'pageTree',
						attributes: {
							contentId,
							dwellTime,
							snippetShown: !!contentValueRef.current,
						},
					},
				}).fire();
			}

			if (contentValueRef.current && dwellTime >= 3000) {
				trackAIResultView({ attributes: { dwellTime } });
			}
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (loading) {
		return (
			<Box xcss={styles.loading} key={`${contentId}-${width}`}>
				<AIStreamLoading padding="space.0" />
			</Box>
		);
	}
	if (!content) {
		return null;
	}

	return (
		<Stack
			xcss={styles.snippet}
			alignBlock="start"
			alignInline="start"
			grow="fill"
			space="space.050"
		>
			<Box xcss={styles.container} key={`${contentId}-${width}`}>
				<Box xcss={isExpanded ? styles.fullText : styles.truncatedText} ref={snippetRef} as="p">
					{isTruncated && !isExpanded && (
						<Box as="span" xcss={styles.seeMore}>
							<LinkButton
								appearance="subtle"
								spacing="compact"
								href="#"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setIsExpanded(!isExpanded);
									return false;
								}}
							>
								<FormattedMessage {...i18n.seeMore} />
							</LinkButton>
						</Box>
					)}
					<Markdown
						css={{
							margin: token('space.0'),
							padding: token('space.0'),
							font: token('font.body.small'),
							display: 'block',
							width: '100%',
						}}
					>
						{content}
					</Markdown>
				</Box>
			</Box>
			{!loading && !!snippet && !isExpanded && <AISnippetFooter showAIFooter={!!snippet} />}
		</Stack>
	);
};
